/**
 * FogComponent manages scene fog effects (linear or exponential).
 * Applies fog to the world scene when the component is playing.
 */
import * as THREE from 'three';

import { EngineClass } from '../../systems/ClassRegistry.js';
import { property } from '../../utils/serialization/decorator.js';
import { NON_SPATIAL, SceneComponent, type SceneComponentOptions} from '../SceneComponent.js';

import type { EditorPropertyChangedResult } from '../../utils/EditorClassMeta.js';

export enum FogMode {
  Linear = 'linear',
  Exponential = 'exponential',
}

export interface FogComponentOptions extends SceneComponentOptions {
  fogMode?: FogMode;
  fogColor?: THREE.Color;
  fogNear?: number;
  fogFar?: number;
  fogDensity?: number;
}

@EngineClass('FogComponent')
export class FogComponent extends SceneComponent {
  static readonly [NON_SPATIAL] = true;


  private fogInstance!: THREE.Fog | THREE.FogExp2;

  @property()
  protected fogMode: FogMode = FogMode.Linear;

  @property()
  protected fogColor: THREE.Color = new THREE.Color(0.5, 0.5, 0.5);

  @property({ type: 'number', min: 0, step: 0.1, decimals: 1, description: 'Near distance for linear fog' })
  protected fogNear: number = 0.1;

  @property({ type: 'number', min: 0, step: 1, decimals: 0, description: 'Far distance for linear fog' })
  protected fogFar: number = 50;

  @property({ type: 'number', min: 0, max: 1, step: 0.01, decimals: 3, description: 'Density for exponential fog' })
  protected fogDensity: number = 0.04;

  constructor() {
    super();
  }

  public override initialize(options?: FogComponentOptions): void {
    super.initialize(options);
    this.fogMode = options?.fogMode ?? this.fogMode;
    this.fogColor = options?.fogColor ?? this.fogColor;
    this.fogNear = options?.fogNear ?? this.fogNear;
    this.fogFar = options?.fogFar ?? this.fogFar;
    this.fogDensity = options?.fogDensity ?? this.fogDensity;
  }

  private createFogInstance(): void {
    switch (this.fogMode) {
      case FogMode.Linear:
        this.fogInstance = new THREE.Fog(this.fogColor, this.fogNear, this.fogFar);
        break;
      case FogMode.Exponential:
        this.fogInstance = new THREE.FogExp2(this.fogColor, this.fogDensity);
        break;
    }
  }

  public override beginPlay(): void {
    super.beginPlay();
    // Recreate fog instance to pick up deserialized property values
    this.createFogInstance();
    this.enableFog();
  }

  public override endPlay(): void {
    super.endPlay();
    this.disableFog();
  }

  public enableFog(): void {
    const world = this.getWorld();
    if (world) {
      world.getRenderScene().fog = this.fogInstance;
    }
  }

  public disableFog(): void {
    const world = this.getWorld();
    if (world) {
      world.getRenderScene().fog = null;
    }
  }

  public setFogColor(color: THREE.Color): this {
    this.fogColor = color;
    this.fogInstance.color = color;
    return this;
  }

  public getFogColor(): THREE.Color {
    return this.fogColor;
  }

  public setFogMode(mode: FogMode): this {
    if (this.fogMode !== mode) {
      this.fogMode = mode;
      this.createFogInstance();
      this.enableFog();
    }
    return this;
  }

  public getFogMode(): FogMode {
    return this.fogMode;
  }

  public setFogNear(near: number): this {
    this.fogNear = near;
    if (this.fogInstance instanceof THREE.Fog) {
      this.fogInstance.near = near;
    }
    return this;
  }

  public getFogNear(): number {
    return this.fogNear;
  }

  public setFogFar(far: number): this {
    this.fogFar = far;
    if (this.fogInstance instanceof THREE.Fog) {
      this.fogInstance.far = far;
    }
    return this;
  }

  public getFogFar(): number {
    return this.fogFar;
  }

  public setFogDensity(density: number): this {
    this.fogDensity = density;
    if (this.fogInstance instanceof THREE.FogExp2) {
      this.fogInstance.density = density;
    }
    return this;
  }

  public getFogDensity(): number {
    return this.fogDensity;
  }

  protected setFogProperties(): void {
    if (this.fogMode === FogMode.Linear) {
      if (this.fogInstance instanceof THREE.Fog === false) {
        this.fogInstance = new THREE.Fog(this.fogColor, this.fogNear, this.fogFar);
      } else {
        (this.fogInstance as THREE.Fog).color = this.fogColor;
        (this.fogInstance as THREE.Fog).near = this.fogNear;
        (this.fogInstance as THREE.Fog).far = this.fogFar;
      }
    } else if (this.fogMode === FogMode.Exponential) {
      if (this.fogInstance instanceof THREE.FogExp2 === false) {
        this.fogInstance = new THREE.FogExp2(this.fogColor, this.fogDensity);
      } else {
        (this.fogInstance as THREE.FogExp2).color = this.fogColor;
        (this.fogInstance as THREE.FogExp2).density = this.fogDensity;
      }
    }
  }

  //====================================================================================================
  // Editor Functions
  //====================================================================================================

  public override onEditorAddToWorld(): void {
    super.onEditorAddToWorld();
    this.createFogInstance();
    this.enableFog();
  }

  public override onEditorRemoveFromWorld(): void {
    super.onEditorRemoveFromWorld();
    this.disableFog();
  }

  public override onEditorPropertyChanged(path: string, value: any, result: EditorPropertyChangedResult): void {
    super.onEditorPropertyChanged(path, value, result);

    // Update fog when relevant properties change
    if (path === 'fogMode' || path === 'fogColor' || path === 'fogNear' ||
      path === 'fogFar' || path === 'fogDensity') {
      (this as any)[path] = value;
      this.setFogProperties();
      this.enableFog();
    }
  }

  //====================================================================================================
  // Editor Functions
  //====================================================================================================

  public override getEditorClassIcon(): string | null {
    return 'Icon_Fog';
  }
}